import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment, useId, useLayoutEffect, useRef, useState } from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { LabelM } from '@sorare/core/src/atoms/typography';
import { useResizeObserver } from '@sorare/core/src/hooks/ui/useResizeObserver';
import { range } from '@sorare/core/src/lib/arrays';

import { SeasonProgressGraph_footballRivalsPublicManagerInterface } from './__generated__/index.graphql';

const Root = styled.div`
  padding: var(--double-unit);
  width: 100%;
`;

const yPositionFirstLine = 8;
const spaceBetweenLines = 32;
const numberOfLines = 5;
const numberOfRanksBetweenLines = 5;
const yPositionLastLine =
  yPositionFirstLine + (numberOfLines - 1) * spaceBetweenLines;
const xPositionRankLabels = 16;
const xPositionLineStart = 32;
const xPositionFirstDataPoint = 40;
const circleRadius = 4;

const getXPositionLastDataPoint = (width: number) => {
  return width - 8;
};

const getXPosition = (width: number, index: number, entriesLength: number) => {
  return (
    xPositionFirstDataPoint +
    (getXPositionLastDataPoint(width) - xPositionFirstDataPoint) *
      (index / Math.max(entriesLength - 1, 1))
  );
};
const getYPosition = (rank: number) => {
  /* The first line should be position 0, but it was not really looking nice
  so product decided to have lines numbered 1, 5, 10, 15 20.
  We manually override the rank 1 to fall on the first line
  */
  return rank === 1
    ? yPositionFirstLine
    : yPositionFirstLine +
        (rank * spaceBetweenLines) / numberOfRanksBetweenLines;
};

type PromotedZoneProps = {
  width: number;
  promotedCount: number;
  gradientId: string;
};
const PromotedZone = ({
  width,
  promotedCount,
  gradientId,
}: PromotedZoneProps) => {
  return (
    <rect
      x={xPositionLineStart}
      y={yPositionFirstLine}
      width={width - xPositionLineStart}
      height={getYPosition(promotedCount) - yPositionFirstLine}
      fill={`url(#${gradientId})`}
      fillOpacity={0.2}
    />
  );
};

type RelegatedZoneProps = {
  width: number;
  relegatedCount: number;
  maximumSize: number;
  gradientId: string;
};
const RelegatedZone = ({
  width,
  relegatedCount,
  maximumSize,
  gradientId,
}: RelegatedZoneProps) => {
  return (
    <rect
      x={xPositionLineStart}
      y={getYPosition(maximumSize - relegatedCount)}
      width={width - xPositionLineStart}
      height={yPositionLastLine - getYPosition(maximumSize - relegatedCount)}
      fill={`url(#${gradientId})`}
      fillOpacity={0.2}
    />
  );
};

type HorizontalLinesProps = {
  width: number;
};
const HorizontalLines = ({ width }: HorizontalLinesProps) => {
  return range(numberOfLines).map(i => {
    const yPos = i * spaceBetweenLines + yPositionFirstLine;
    return (
      <Fragment key={i}>
        <LabelM
          bold
          as="text"
          x={xPositionRankLabels}
          y={yPos}
          fill="var(--c-nd-800)"
          dominantBaseline="middle"
          textAnchor="end"
        >
          <FormattedNumber
            value={i === 0 ? 1 : i * numberOfRanksBetweenLines}
          />
        </LabelM>
        <line
          x1={xPositionLineStart}
          y1={yPos}
          x2={width}
          y2={yPos}
          stroke="var(--c-nd-600)"
          strokeWidth="0.5"
        />
      </Fragment>
    );
  });
};

type DateLabelsProps = {
  entries: SeasonProgressGraph_footballRivalsPublicManagerInterface['rankingHistory'];
  width: number;
};
const DateLabels = ({ entries, width }: DateLabelsProps) => {
  return entries.map(({ date }, i) => {
    return (
      <LabelM
        bold
        as="text"
        key={date.toString()}
        fill="var(--c-nd-800)"
        x={getXPosition(width, i, entries.length)}
        y={168}
        textAnchor="middle"
      >
        <FormattedDate value={date} weekday="narrow" />
      </LabelM>
    );
  });
};

type CirclesProps = {
  entries: SeasonProgressGraph_footballRivalsPublicManagerInterface['rankingHistory'];
  width: number;
};
const Circles = ({ width, entries }: CirclesProps) => {
  return entries.map(({ ranking }, i) => {
    return (
      <circle
        // eslint-disable-next-line react/no-array-index-key
        key={i}
        fill="var(--c-rivals-reputation)"
        r={circleRadius}
        cx={getXPosition(width, i, entries.length)}
        cy={getYPosition(ranking)}
      />
    );
  });
};

type LineProps = {
  entries: SeasonProgressGraph_footballRivalsPublicManagerInterface['rankingHistory'];
  width: number;
};
const Line = ({ width, entries }: LineProps) => {
  const entriesWithoutFirstOne = entries.slice(1);
  return (
    <path
      stroke="var(--c-rivals-reputation)"
      fill="none"
      d={`M ${xPositionFirstDataPoint} ${getYPosition(
        entries[0]?.ranking
      )} ${entriesWithoutFirstOne.map(({ ranking }, i) => {
        return `${getXPosition(width, i + 1, entries.length)} ${getYPosition(
          ranking
        )}`;
      })}`}
    />
  );
};

type Props = {
  manager?: SeasonProgressGraph_footballRivalsPublicManagerInterface;
};
export const SeasonProgressGraph = ({ manager }: Props) => {
  const [width, setWidth] = useState(540);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const promotedGradientId = useId();
  const relegatedGradientId = useId();
  const { currentLeaderboard, rankingHistory } = manager || {};
  const {
    promotedCount = 0,
    relegatedCount = 0,
    maximumSize = 20,
  } = currentLeaderboard?.leaderboardConfig || {};

  useLayoutEffect(() => {
    const currentWidth = wrapperRef.current?.offsetWidth;
    if (currentWidth) {
      setWidth(currentWidth);
    }
  }, []);

  useResizeObserver(wrapperRef, () => {
    const newWidth = wrapperRef.current?.offsetWidth;
    if (newWidth) {
      setWidth(newWidth);
    }
  });

  if (!rankingHistory?.length) {
    return null;
  }

  return (
    <Root>
      <div ref={wrapperRef}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={width}
          height="168"
          viewBox={`0 0 ${width} 168`}
        >
          <defs>
            {!!promotedCount && (
              <linearGradient
                id={promotedGradientId}
                x1="318"
                y1="20.3091"
                x2="-146"
                y2="16"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="var(--c-rivals-friendlies)" />
                <stop
                  offset="1"
                  stopColor="var(--c-rivals-friendlies)"
                  stopOpacity="0"
                />
              </linearGradient>
            )}
            {!!relegatedCount && (
              <linearGradient
                id={relegatedGradientId}
                x1="318"
                y1="20.3091"
                x2="-146"
                y2="16"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="var(--c-rivals-loss)" />
                <stop
                  offset="1"
                  stopColor="var(--c-rivals-loss)"
                  stopOpacity="0"
                />
              </linearGradient>
            )}
          </defs>
          {!!promotedCount && (
            <PromotedZone
              width={width}
              promotedCount={promotedCount}
              gradientId={promotedGradientId}
            />
          )}
          {!!relegatedCount && (
            <RelegatedZone
              width={width}
              relegatedCount={relegatedCount}
              maximumSize={maximumSize}
              gradientId={relegatedGradientId}
            />
          )}
          <HorizontalLines width={width} />
          <DateLabels width={width} entries={rankingHistory} />
          <Circles width={width} entries={rankingHistory} />
          <Line width={width} entries={rankingHistory} />
        </svg>
      </div>
    </Root>
  );
};

SeasonProgressGraph.fragments = {
  footballRivalsPublicManagerInterface: gql`
    fragment SeasonProgressGraph_footballRivalsPublicManagerInterface on FootballRivalsPublicManagerInterface {
      currentLeaderboard {
        id
        leaderboardConfig {
          promotedCount
          relegatedCount
          maximumSize
        }
      }
      rankingHistory {
        date
        ranking
      }
    }
  ` as TypedDocumentNode<SeasonProgressGraph_footballRivalsPublicManagerInterface>,
};
